// WP-0.7 (S-01): bootstrap the single owner. Centered card; setup-token (from server
// console) then owner creation — name, password, and passkey registration with a
// TOTP fallback (AU-A/B). Reachable only while no owner exists (guarded in App).
import { startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { markAuthenticated } from '../lib/session';
import { AuthCard } from '../components/AuthCard';
import { Field, inputClass, primaryButtonClass } from '../components/FormKit';

interface StartForm {
  setupToken: string;
  name: string;
  password: string;
}

interface SetupStartResponse {
  totpSecret: string;
  otpauthUrl: string;
  webauthnOptions: unknown;
}

export function SetupPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ start: StartForm; totp: SetupStartResponse } | null>(
    null,
  );
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StartForm>();

  const startMutation = useMutation({
    mutationFn: (form: StartForm) => api.post<SetupStartResponse>('/api/v1/auth/setup/start', form),
    onSuccess: (data, form) => {
      setPending({ start: form, totp: data });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Setup failed');
    },
  });

  const finishMutation = useMutation({
    mutationFn: async () => {
      if (pending === null) throw new Error('no pending setup');
      let attestation: unknown;
      try {
        attestation = await startRegistration({
          optionsJSON: pending.totp.webauthnOptions as Parameters<
            typeof startRegistration
          >[0]['optionsJSON'],
        });
      } catch {
        attestation = undefined; // passkey optional — TOTP fallback still completes setup
      }
      await api.post('/api/v1/auth/setup/finish', {
        setupToken: pending.start.setupToken,
        totpCode,
        ...(attestation !== undefined ? { attestation } : {}),
      });
    },
    onSuccess: () => {
      markAuthenticated(queryClient, { onboarded: false }); // setup/finish issued a session cookie (AU-A)
      navigate('/onboarding');
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not finish setup');
    },
  });

  if (pending !== null) {
    return (
      <AuthCard title="Add your authenticator" subtitle="Scan this secret in your TOTP app.">
        <p className="mb-3 break-all rounded-[var(--radius-control)] bg-bg p-2 font-mono text-sm text-ink">
          {pending.totp.totpSecret}
        </p>
        <Field label="6-digit code" htmlFor="totp">
          <input
            id="totp"
            className={inputClass}
            inputMode="numeric"
            value={totpCode}
            onChange={(e) => {
              setTotpCode(e.target.value);
            }}
          />
        </Field>
        {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
        <button
          className={primaryButtonClass}
          disabled={finishMutation.isPending || totpCode.length < 6}
          onClick={() => {
            setError(null);
            finishMutation.mutate();
          }}
        >
          Finish setup
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set up PF-OS" subtitle="Enter the setup token from your server console.">
      <form
        onSubmit={(e) => {
          void handleSubmit((form) => {
            setError(null);
            startMutation.mutate(form);
          })(e);
        }}
      >
        <Field label="Setup token" htmlFor="setupToken" error={errors.setupToken?.message}>
          <input
            id="setupToken"
            className={inputClass}
            {...register('setupToken', { required: 'Required' })}
          />
        </Field>
        <Field label="Name" htmlFor="name" error={errors.name?.message}>
          <input id="name" className={inputClass} {...register('name', { required: 'Required' })} />
        </Field>
        <Field label="Password" htmlFor="password" error={errors.password?.message}>
          <input
            id="password"
            type="password"
            className={inputClass}
            {...register('password', {
              required: 'Required',
              minLength: { value: 12, message: 'Use at least 12 characters' },
            })}
          />
        </Field>
        {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
        <button className={primaryButtonClass} disabled={startMutation.isPending} type="submit">
          Create owner
        </button>
      </form>
    </AuthCard>
  );
}
